/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { commonStyles } from '../../styles/commonStyles';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { HistoryOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import MainLayout from '../../components/common/MainLayout';
import { useGetBedByIdQuery, useUpdateBedMutation, useGetBedRotationHistoryQuery, useGetBedPropertiesQuery } from '../../api/fieldsApi';
import type { BedProperty } from '../../api/fieldsApi';
import {
  useGetPlantingsQuery,
  useCreatePlantingMutation,
  useUpdatePlantingMutation,
} from '../../api/plantingsApi';
import {
  useLazyGetBedEventsQuery,
  useCreateEventFromTemplateMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '../../api/plantingEventsApi';
import type { PlantingEvent } from '../../types/plantingEvents';
import { useGetSaveRuntimeSessionQuery } from '../../api/simulationApi';
import { useGetEventTemplatesQuery } from '../../api/eventTemplatesApi';
import type { Bed } from '../../models/Bed';
import type { Planting, PlantingPayload, PlantingUpdatePayload, PlantingLifecycleStatus } from '../../models/Planting';
import PlantingFormModal from '../../features/plantings/components/PlantingFormModal';
import PlantingsList from '../../components/plantings/PlantingsList';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import RotationHistoryBar from '../../components/rotation/RotationHistoryBar';
import BedEnvironmentCard from './components/BedEnvironmentCard';
import ScenarioDayControl from '../../components/scenarios/ScenarioDayControl';
import { toastError, toastSuccess } from '../../utils/toast';
import { hasAction } from '../../types/permissions';
import {
  useCreateSoilMeasurementByBedMutation,
  useGetEffectiveSoilStateByBedQuery,
  useGetSoilMeasurementsByBedQuery,
  useGetSoilStateByBedQuery,
  useUpsertSoilStateByBedMutation,
} from '../../api/soilStateApi';
import type { SoilMetrics } from '../../types/soil';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { buildScenarioBed, buildScenarioPlanting, buildScenarioWorldIndexes, withPlantingRuntime } from '../../utils/scenarioWorldAdapters';

const { Text } = Typography;

const SOIL_METRIC_LABELS: Array<{ key: keyof SoilMetrics; label: string; suffix?: string }> = [
  { key: 'moisturePct', label: 'Влажность', suffix: '%' },
  { key: 'temperatureC', label: 'Температура', suffix: '°C' },
  { key: 'pH', label: 'pH' },
  { key: 'nitrogen', label: 'Азот' },
  { key: 'phosphorus', label: 'Фосфор' },
  { key: 'potassium', label: 'Калий' },
  { key: 'organicMatter', label: 'Органика', suffix: '%' },
  { key: 'compaction', label: 'Уплотнение', suffix: '%' },
  { key: 'salinity', label: 'Засолённость' },
];

const SOIL_SOURCE_LABELS: Record<string, string> = {
  default: 'По умолчанию',
  estimated: 'Оценка',
  measured: 'Замер',
  derived: 'Производное',
  manual: 'Вручную',
  lab: 'Лаборатория',
};

const formatSoilMetric = (value?: number | null, suffix?: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value}${suffix ?? ''}`;
};

const EVENT_NAME_BY_KIND: Record<string, string> = {
  transplant: 'Перемещение посадки',
  relocation: 'Перемещение посадки',
  created: 'Создание посадки',
  status_changed: 'Изменение статуса',
  watering: 'Полив',
  fertilizing: 'Удобрение',
  state_correction: 'Исправление показателя',
  pruning: 'Обрезка',
  health_check: 'Проверка здоровья',
  pest_inspection: 'Проверка на вредителей',
  disease_inspection: 'Проверка на болезни',
};

const getEventDisplayName = (event: any): string => {
  if (event.templateName) return event.templateName;
  if (event.kind && EVENT_NAME_BY_KIND[event.kind]) return EVENT_NAME_BY_KIND[event.kind];
  if (event.kind === 'measurement') return 'Измерение';
  if (event.kind === 'service') return 'Обслуживание';
  return event.type || 'Событие';
};

const resolveEventDate = (event: PlantingEvent): string | null => {
  return event.actualDate || event.plannedDate || null;
};

const toDateOnly = (value?: string | null): string | null => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

const toDayjsDateOnly = (value?: string | null): dayjs.Dayjs | null => {
  const date = toDateOnly(value);
  if (!date) return null;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed : null;
};

const formatDateOnlyRu = (value?: string | null): string => {
  const parsed = toDayjsDateOnly(value);
  return parsed ? parsed.format('DD.MM.YYYY') : String(value || '—');
};

const resolveEditableEventDateField = (event: PlantingEvent): 'actualDate' | 'plannedDate' => {
  return event.status === 'completed' ? 'actualDate' : 'plannedDate';
};

const resolveEditableEventDateLabel = (event?: PlantingEvent | null): string => {
  if (!event) return 'Дата события';
  return event.status === 'completed' ? 'Дата выполнения' : 'Плановая дата';
};

const ScenarioBedPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveId, bedId } = useParams<{
    saveId: string;
    bedId: string;
  }>();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();

  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const rawBed = world.structure.beds.find((item) => String(item.id) === String(bedId));
  const runtimeBed = bedId ? world.runtime?.beds?.[String(bedId)] : null;

  const plantings = useMemo(() => {
    if (!rawBed) return [];
    return world.structure.plantings
      .filter((planting) => String(planting.bedId || '') === String(rawBed.id))
      .map((planting) => buildScenarioPlanting(
        withPlantingRuntime(planting, world.runtime?.plantings?.[String(planting.id)]),
        indexes,
      ));
  }, [indexes, rawBed, world.runtime?.plantings, world.structure.plantings]);

  if (!rawBed) {
    return (
      <MainLayout>
        <Card>Не удалось загрузить данные грядки</Card>
      </MainLayout>
    );
  }

  const bed = buildScenarioBed(rawBed, indexes);
  const plotName = bed.plotSummary?.name ?? bed.plotSummary?.id ?? '—';
  const fieldName = bed.fieldSummary?.name ?? bed.fieldSummary?.id ?? '—';

  return (
    <MainLayout>
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <ScenarioDayControl saveId={saveId} />

        <Alert
          type="info"
          showIcon
          message="Грядка сценария"
          description="Детали грядки доступны в режиме read-only и строятся из scenario world snapshot."
        />

        <Card
          title={bed.name}
          extra={<Button onClick={() => { void refreshWorld(); }}>Обновить</Button>}
        >
          <Space direction="vertical" size={6}>
            <Text type="secondary">Участок: {plotName} · Поле: {fieldName}</Text>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Площадь">{bed.area ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Влажность">
                {runtimeBed?.moisturePct != null ? `${runtimeBed.moisturePct}%` : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        {bedId && saveId && <BedEnvironmentCard bedId={bedId} saveId={saveId} />}

        <Card title={`Посадки (${plantings.length})`}>
          {plantings.length === 0 ? (
            <Empty description="В этой грядке пока нет посадок" />
          ) : (
            <PlantingsList
              plantings={plantings}
              groupByCrop={false}
              onView={(planting) => navigate(`/saves/${saveId}/plantings/${planting.uuid ?? planting.id}`)}
            />
          )}
        </Card>

        <Card title="Применённые события">
          {runtimeBed?.appliedEvents && runtimeBed.appliedEvents.length > 0 ? (
            <List
              dataSource={runtimeBed.appliedEvents}
              renderItem={(event) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text strong>{event.action || 'Событие'}</Text>
                    <Text type="secondary">{event.date || 'Дата не указана'}</Text>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Для этой грядки пока нет применённых событий" />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundBedPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveId, bedId } = useParams<{
    saveId: string;
    bedId: string;
  }>();
  const save = useSave();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state: any) => state.auth.user);

  const {
    data: bedData,
    isLoading: bedLoading,
    isError: bedError,
    refetch: refetchBed,
  } = useGetBedByIdQuery(
    { bedId: bedId ?? '' },
    { skip: !bedId, refetchOnMountOrArgChange: true }
  );

  const bed: Bed | undefined = bedData;

  const farmSummary = bed?.farmSummary;
  const fieldSummary = bed?.fieldSummary;
  const plotSummary = bed?.plotSummary;

  const resolvedFarmId = save.farmId ?? farmSummary?.id;
  const resolvedFieldId = fieldSummary?.id;
  const resolvedPlotId = plotSummary?.id;

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(
    save.id,
    { skip: !save.id || !isAuthenticated }
  );
  const simSessionId = activeSimSession?.id ?? null;

  const {
    data: rotationHistory,
  } = useGetBedRotationHistoryQuery(
    bedId ? { bedId, simSessionId } : skipToken as any,
  );

  const {
    data: bedProperties = [],
    isLoading: bedPropertiesLoading,
  } = useGetBedPropertiesQuery(
    bedId ? { bedId, simSessionId } : skipToken as any,
    { skip: !bedId }
  );

  const farmName = farmSummary?.name ?? save.farmName ?? `Сейв ${save.id.slice(0, 8)}`;
  const fieldName = fieldSummary?.name ?? `Поле ${resolvedFieldId}`;
  const plotName = plotSummary?.name ?? `Участок ${resolvedPlotId}`;
  const bedName = bed?.name ?? `BED-${bedId}`;

  const [includeHistoricalPlantings, setIncludeHistoricalPlantings] = useState(false);

  const plantingsQueryArg = useMemo(() => {
    if (!bedId) return skipToken;
    const query: {
      saveId: string;
      bedId: string;
      limit: number;
      offset: number;
      lifecycleStatus?: PlantingLifecycleStatus;
      simSessionId?: string;
    } = {
      saveId: saveId || '',
      bedId,
      limit: includeHistoricalPlantings ? 200 : 100,
      offset: 0,
    };
    if (!includeHistoricalPlantings) {
      query.lifecycleStatus = 'active';
      if (simSessionId) {
        query.simSessionId = simSessionId;
      }
    }
    return query;
  }, [bedId, includeHistoricalPlantings, saveId, simSessionId]);

  const {
    data: plantings = [],
    isLoading: plantingsLoading,
    isError: plantingsError,
    refetch: refetchPlantings,
  } = useGetPlantingsQuery(plantingsQueryArg, {
    skip: !bedId,
  });

  const [createPlanting, { isLoading: creatingPlanting }] = useCreatePlantingMutation();
  const [updatePlanting, { isLoading: updatingPlanting }] = useUpdatePlantingMutation();
  const [updateBed] = useUpdateBedMutation();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlanting, setEditingPlanting] = useState<Planting | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [bedSettingsOpen, setBedSettingsOpen] = useState(false);
  const [bedForm] = Form.useForm();
  const [soilStateOpen, setSoilStateOpen] = useState(false);
  const [soilMeasurementOpen, setSoilMeasurementOpen] = useState(false);
  const [soilStateForm] = Form.useForm();
  const [soilMeasurementForm] = Form.useForm();

  const [fetchBedEvents, { data: bedEvents = [], isLoading: loadingBedEvents, isFetching: fetchingBedEvents }] =
    useLazyGetBedEventsQuery();
  const [updateEvent] = useUpdateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();

  const [editingEvent, setEditingEvent] = useState<PlantingEvent | null>(null);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [editEventForm] = Form.useForm();

  const {
    data: soilState,
    isLoading: soilStateLoading,
  } = useGetSoilStateByBedQuery(bedId ?? skipToken as any, {
    skip: !bedId,
  });

  const {
    data: effectiveSoilState,
    isLoading: effectiveSoilStateLoading,
  } = useGetEffectiveSoilStateByBedQuery(
    bedId ? { bedId } : skipToken as any,
    { skip: !bedId }
  );

  const {
    data: soilMeasurements = [],
    isLoading: soilMeasurementsLoading,
  } = useGetSoilMeasurementsByBedQuery(
    bedId ? { bedId, limit: 10 } : skipToken as any,
    { skip: !bedId }
  );

  const [upsertSoilState, { isLoading: savingSoilState }] = useUpsertSoilStateByBedMutation();
  const [createSoilMeasurement, { isLoading: savingSoilMeasurement }] = useCreateSoilMeasurementByBedMutation();
  const [createEventFromTemplate] = useCreateEventFromTemplateMutation();

  // Load event templates (save-scoped, Wave 5.1).
  const { data: eventTemplatesData } = useGetEventTemplatesQuery(
    { saveId: save?.id || '' },
    { skip: !save?.id }
  );
  const soilMeasurementTemplate = useMemo(() => {
    if (!eventTemplatesData?.templates) return null;
    return eventTemplatesData.templates.find((t: any) => t.code === 'soil_measurement') ?? null;
  }, [eventTemplatesData]);

  // Permission checks
  const canCreatePlantings = hasAction(user, 'plantings', 'plantings.create', String(resolvedFarmId));
  const canUpdatePlantings = hasAction(user, 'plantings', 'plantings.update', String(resolvedFarmId));
  const canUpdateBed = hasAction(user, 'beds', 'beds.update', String(resolvedFarmId));

  const openSoilStateModal = () => {
    soilStateForm.setFieldsValue({
      sourceType: soilState?.sourceType ?? 'manual',
      observedAt: soilState?.observedAt ?? undefined,
      confidencePct: soilState?.confidencePct ?? undefined,
      notes: soilState?.notes ?? undefined,
      ...soilState?.metrics,
    });
    setSoilStateOpen(true);
  };

  const openSoilMeasurementModal = () => {
    soilMeasurementForm.resetFields();
    soilMeasurementForm.setFieldsValue({
      sourceType: 'measured',
    });
    setSoilMeasurementOpen(true);
  };

  const defaultPlantingLocations = useMemo(() => {
    if (!bed) return undefined;
    
    // Используем resolved ID чтобы гарантировать наличие значений
    const effectivePlotId = resolvedPlotId ?? bed.plotId;
    const effectiveFieldId = resolvedFieldId;
    const effectiveFarmId = resolvedFarmId;
    
    return [
      {
        id: undefined,
        bedId: bed.id,
        bed: {
          id: bed.id,
          plot_id: effectivePlotId,
          row_number: bed.row_number ?? null,
          position_number: bed.position_number ?? null,
        },
        plot: effectivePlotId
          ? {
              id: effectivePlotId,
              name: plotSummary?.name ?? plotName,
              field_id: effectiveFieldId,
            }
          : undefined,
        field: effectiveFieldId
          ? {
              id: effectiveFieldId,
              name: fieldSummary?.name ?? fieldName,
              farm_id: effectiveFarmId,
            }
          : undefined,
        farm: effectiveFarmId
          ? {
              id: effectiveFarmId,
              name: farmSummary?.name ?? farmName,
            }
          : undefined,
      },
    ];
  }, [bed, resolvedPlotId, resolvedFieldId, resolvedFarmId, plotSummary, fieldSummary, farmSummary, plotName, fieldName, farmName]);

  const handleOpenCreateModal = () => {
    setEditingPlanting(null);
    setCreateModalOpen(true);
  };

  const handleOpenEditModal = (planting: Planting) => {
    setEditingPlanting(planting);
    setEditModalOpen(true);
  };

  const handleCreatePlantingSubmit = async (payload: PlantingPayload | PlantingUpdatePayload) => {
    try {
      if (!payload.locations?.length) {
        throw new Error('Не указаны локации посадки');
      }
      await createPlanting({ ...(payload as PlantingPayload), saveId: saveId || '' }).unwrap();
      toastSuccess('Посадка создана');
      setCreateModalOpen(false);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleUpdatePlantingSubmit = async (payload: PlantingUpdatePayload) => {
    if (!editingPlanting) return;
    try {
      await updatePlanting({ saveId: saveId || '', id: editingPlanting.id, payload }).unwrap();
      toastSuccess('Посадка обновлена');
      setEditModalOpen(false);
      setEditingPlanting(null);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleOpenBedSettings = () => {
    if (!bed) return;
    bedForm.setFieldsValue({
      name: bed.name,
    });
    setBedSettingsOpen(true);
  };

  const handleSaveBedSettings = async () => {
    if (!bed || !bedId) return;
    try {
      const values = await bedForm.validateFields();
      await updateBed({ id: bedId, payload: values }).unwrap();
      toastSuccess('Грядка обновлена');
      setBedSettingsOpen(false);
      refetchBed();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveSoilState = async () => {
    if (!bedId) return;
    try {
      const values = await soilStateForm.validateFields();
      const metrics = SOIL_METRIC_LABELS.reduce<Record<string, number | null>>((acc, item) => {
        acc[item.key] = values[item.key] ?? null;
        return acc;
      }, {});
      await upsertSoilState({
        bedId,
        payload: {
          sourceType: values.sourceType,
          observedAt: values.observedAt ?? null,
          confidencePct: values.confidencePct ?? null,
          notes: values.notes ?? null,
          metrics,
        },
      }).unwrap();
      toastSuccess('Состояние почвы сохранено');
      setSoilStateOpen(false);
    } catch (error) {
      toastError(error);
    }
  };

  const handleCreateSoilMeasurement = async () => {
    if (!bedId) return;
    try {
      const values = await soilMeasurementForm.validateFields();

      // If soil_measurement template is available, create event (new event model)
      if (soilMeasurementTemplate && resolvedFarmId) {
        // Map form values to property IDs from template
        const properties: Record<string, number> = {};
        const templateProps = soilMeasurementTemplate.properties || [];
        const SOIL_CODE_TO_FORM_KEY: Record<string, keyof SoilMetrics> = {
          soil_ph: 'pH',
          soil_moisture: 'moisturePct',
          soil_nitrogen: 'nitrogen',
          soil_phosphorus: 'phosphorus',
          soil_potassium: 'potassium',
          soil_organic_matter: 'organicMatter',
          soil_compaction: 'compaction',
          soil_salinity: 'salinity',
          soil_temp: 'temperatureC',
        };
        for (const tp of templateProps) {
          const code = tp.code;
          const formKey = SOIL_CODE_TO_FORM_KEY[code];
          if (formKey && values[formKey] !== undefined && values[formKey] !== null) {
            properties[String(tp.propertyId)] = values[formKey];
          }
        }
        await createEventFromTemplate({
          saveId: save.id,
          payload: {
            templateId: soilMeasurementTemplate.id,
            targets: [{ bedId }],
            properties,
            notes: values.notes ?? undefined,
          },
        }).unwrap();
        toastSuccess('Замер почвы записан как событие');
        setSoilMeasurementOpen(false);
        soilMeasurementForm.resetFields();
        return;
      }

      // Fallback: legacy soil_state_measurements
      const metrics = SOIL_METRIC_LABELS.reduce<Record<string, number | null>>((acc, item) => {
        acc[item.key] = values[item.key] ?? null;
        return acc;
      }, {});
      await createSoilMeasurement({
        bedId,
        payload: {
          sourceType: values.sourceType,
          measuredAt: values.measuredAt,
          sampleDepthFromCm: values.sampleDepthFromCm ?? null,
          sampleDepthToCm: values.sampleDepthToCm ?? null,
          confidencePct: values.confidencePct ?? null,
          notes: values.notes ?? null,
          metrics,
        },
      }).unwrap();
      toastSuccess('Замер почвы добавлен');
      setSoilMeasurementOpen(false);
      soilMeasurementForm.resetFields();
    } catch (error) {
      toastError(error);
    }
  };

  const handleLoadBedHistory = async () => {
    if (!bedId) return;
    await fetchBedEvents({ bedId }).unwrap();
    setHistoryLoaded(true);
  };

  const handleOpenEditEvent = (event: PlantingEvent) => {
    const initialDateRaw = resolveEventDate(event);
    const fallbackDate = save.cursor?.simDate || save.cursor?.sim_date || activeSimSession?.simDate || null;
    const initialDate = toDayjsDateOnly(initialDateRaw) || toDayjsDateOnly(fallbackDate);

    setEditingEvent(event);
    setEditEventOpen(true);
    editEventForm.setFieldsValue({
      eventDate: initialDate && initialDate.isValid() ? initialDate : null,
      notes: event.notes || '',
    });
  };

  const handleSaveEventCorrection = async () => {
    if (!editingEvent) return;

    try {
      const values = await editEventForm.validateFields();
      const eventDate = values.eventDate?.format('YYYY-MM-DD');
      const dateField = resolveEditableEventDateField(editingEvent);
      setSavingEvent(true);

      await updateEvent({
        saveId: save.id,
        eventId: editingEvent.id,
        payload: {
          notes: values.notes?.trim() || '',
          [dateField]: eventDate,
        },
      }).unwrap();

      if (bedId) {
        await fetchBedEvents({ bedId }).unwrap();
      }
      setEditEventOpen(false);
      setEditingEvent(null);
      toastSuccess('Событие скорректировано');
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent({ saveId: save.id, eventId }).unwrap();
      if (bedId) {
        await fetchBedEvents({ bedId }).unwrap();
      }
      toastSuccess('Событие удалено');
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainLayout>
      {bedLoading ? (
        <Spin />
      ) : bedError || !bed ? (
        <Card>Не удалось загрузить данные грядки</Card>
      ) : (
        <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
          <Card
            title={bedName}
            extra={
              canUpdateBed && (
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={handleOpenBedSettings}
                  size="small"
                >
                  Настройки
                </Button>
              )
            }
          >
            <Space direction="vertical" size={6}>
              <Text type="secondary">
                Участок: {plotName} · Поле: {fieldName}
              </Text>
              <Space direction="vertical" size={2}>
                {bed.length !== undefined && bed.length !== null && (
                  <Text>Длина: {bed.length} м</Text>
                )}
                {bed.area !== undefined && bed.area !== null && <Text>Площадь: {bed.area} м²</Text>}
                {bed.row_number !== undefined && bed.row_number !== null && (
                  <Text>Ряд: {bed.row_number}</Text>
                )}
                {bed.position_number !== undefined && bed.position_number !== null && (
                  <Text>Позиция: {bed.position_number}</Text>
                )}
                {bed.geo_location && <Text>Геолокация: {bed.geo_location}</Text>}
                {bed.description && <Text>Описание: {bed.description}</Text>}
              </Space>
            </Space>
          </Card>

          {rotationHistory && (
            <Card title="Севооборот" size="small">
              <RotationHistoryBar
                entries={rotationHistory.entries}
                families={rotationHistory.families}
                simDate={rotationHistory.simDate}
              />
            </Card>
          )}

          <Card
            title="Почва"
            extra={
              canUpdateBed && (
                <Space>
                  <Button onClick={openSoilMeasurementModal}>Добавить замер</Button>
                  <Button type="primary" onClick={openSoilStateModal}>Редактировать состояние</Button>
                </Space>
              )
            }
          >
            <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
              <Card type="inner" title="Свойства грядки (CPV)" size="small">
                {bedPropertiesLoading ? (
                  <Spin />
                ) : bedProperties.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет свойств. Добавьте замер почвы чтобы обновить показатели." />
                ) : (
                  <Descriptions size="small" column={2} bordered>
                    {bedProperties.map((prop: BedProperty) => (
                      <Descriptions.Item
                        key={prop.propertyId}
                        label={prop.propertyName}
                      >
                        {prop.valueNumber !== null
                          ? `${prop.valueNumber}${prop.unit ? ` ${prop.unit}` : ''}`
                          : prop.valueText !== null
                          ? prop.valueText
                          : prop.valueBoolean !== null
                          ? (prop.valueBoolean ? 'Да' : 'Нет')
                          : '—'}
                        {prop.valueDate && (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {new Date(prop.valueDate).toLocaleDateString('ru-RU')}
                          </Text>
                        )}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                )}
              </Card>

              <Card type="inner" title="Текущее состояние (legacy)" size="small">
                {soilStateLoading || effectiveSoilStateLoading ? (
                  <Spin />
                ) : soilState ? (
                  <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
                    <Space wrap>
                      <Tag color="blue">Источник: {SOIL_SOURCE_LABELS[soilState.sourceType] ?? soilState.sourceType}</Tag>
                      {soilState.confidencePct !== null && soilState.confidencePct !== undefined && (
                        <Tag color="gold">Доверие: {soilState.confidencePct}%</Tag>
                      )}
                      {effectiveSoilState?.resolvedAt && (
                        <Tag>Эффективно на {new Date(effectiveSoilState.resolvedAt).toLocaleDateString('ru-RU')}</Tag>
                      )}
                    </Space>
                    <Descriptions size="small" column={2} bordered>
                      {SOIL_METRIC_LABELS.map((item) => (
                        <Descriptions.Item key={item.key} label={item.label}>
                          {formatSoilMetric(soilState.metrics[item.key], item.suffix)}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                    {soilState.notes && <Text type="secondary">{soilState.notes}</Text>}
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Состояние почвы ещё не задано" />
                )}
              </Card>


              <Card type="inner" title="Последние замеры" size="small">
                {soilMeasurementsLoading ? (
                  <Spin />
                ) : soilMeasurements.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Замеров пока нет" />
                ) : (
                  <List
                    dataSource={soilMeasurements}
                    renderItem={(measurement) => (
                      <List.Item>
                        <List.Item.Meta
                          title={`${SOIL_SOURCE_LABELS[measurement.sourceType] ?? measurement.sourceType} · ${new Date(measurement.measuredAt).toLocaleString('ru-RU')}`}
                          description={
                            <Space direction="vertical" size={2}>
                              <Text type="secondary">
                                Глубина: {formatSoilMetric(measurement.sampleDepthFromCm, ' см')} — {formatSoilMetric(measurement.sampleDepthToCm, ' см')}
                              </Text>
                              <Text type="secondary">
                                Влажность: {formatSoilMetric(measurement.metrics.moisturePct, '%')} · pH: {formatSoilMetric(measurement.metrics.pH)} · Азот: {formatSoilMetric(measurement.metrics.nitrogen)}
                              </Text>
                              {measurement.notes && <Text type="secondary">{measurement.notes}</Text>}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Space>
          </Card>

          {resolvedFarmId && bedId && (
            <BedEnvironmentCard bedId={bedId} saveId={save.id} />
          )}

          <Card
            title="Посадки на грядке"
            extra={
              <Space>
                {canCreatePlantings && (
                  <Button type="primary" onClick={handleOpenCreateModal}>
                    Добавить посадку
                  </Button>
                )}
                <Button onClick={() => navigate(`/saves/${saveId}/crops`)}>
                  Управление культурами
                </Button>
                <Space size={12} align="center">
                  <Button onClick={() => refetchPlantings()}>Обновить</Button>
                  <Space size={4} align="center">
                    <Switch
                      size="small"
                      checked={includeHistoricalPlantings}
                      onChange={setIncludeHistoricalPlantings}
                    />
                    <Text type="secondary">
                      {includeHistoricalPlantings ? 'Показаны все посадки' : 'Только активные'}
                    </Text>
                  </Space>
                </Space>
              </Space>
            }
          >
            {plantingsLoading ? (
              <Spin />
            ) : plantingsError ? (
              <Card type="inner">Не удалось загрузить посадки</Card>
            ) : (
              <PlantingsList
                plantings={plantings}
                loading={plantingsLoading}
                viewMode="gallery"
                farmId={resolvedFarmId}
                groupByCrop={false}
                showLocationCode={false}
                onEdit={canUpdatePlantings ? handleOpenEditModal : undefined}
              />
            )}
          </Card>

          <Card
            title="История грядки"
            extra={
              <Button
                icon={<HistoryOutlined />}
                loading={loadingBedEvents || fetchingBedEvents}
                onClick={handleLoadBedHistory}
              >
                {historyLoaded ? 'Обновить' : 'Загрузить'}
              </Button>
            }
          >
            {!historyLoaded && !loadingBedEvents && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Загрузите историю, чтобы увидеть все действия по этой грядке, включая прошлые периоды"
              />
            )}

            {(loadingBedEvents || fetchingBedEvents) && <Spin />}

            {historyLoaded && !loadingBedEvents && bedEvents.length === 0 && (
              <Text type="secondary">Событий по грядке пока нет</Text>
            )}

            {historyLoaded && !loadingBedEvents && bedEvents.length > 0 && (
              <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
                {bedEvents.map((event) => {
                  const eventDate = event.actualDate || event.plannedDate || event.createdAt;
                  return (
                    <Card key={event.id} size="small">
                      <Space direction="vertical" size={2} css={commonStyles.fullWidth}>
                        <Space size={8} wrap>
                          <Text strong>{getEventDisplayName(event)}</Text>
                          {eventDate && (
                            <Text type="secondary">
                              {formatDateOnlyRu(eventDate)}
                            </Text>
                          )}
                          <Space size={6}>
                            <Button size="small" onClick={() => handleOpenEditEvent(event)}>
                              Скорректировать
                            </Button>
                            <Popconfirm
                              title="Удалить событие?"
                              description="Запись исчезнет из истории. Действие необратимо."
                              okText="Удалить"
                              cancelText="Отмена"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => handleDeleteEvent(event.id)}
                            >
                              <Button size="small" danger>
                                Удалить
                              </Button>
                            </Popconfirm>
                          </Space>
                        </Space>
                        {event.notes ? <Text type="secondary">{event.notes}</Text> : null}
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            )}
          </Card>
        </Space>
      )}

      <Modal
        title="Корректировка события"
        open={editEventOpen}
        onOk={handleSaveEventCorrection}
        onCancel={() => { setEditEventOpen(false); setEditingEvent(null); }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingEvent}
      >
        <Form form={editEventForm} layout="vertical">
          <Form.Item
            label={resolveEditableEventDateLabel(editingEvent)}
            name="eventDate"
            rules={[{ required: true, message: 'Укажите дату события' }]}
          >
            <DatePicker format="DD.MM.YYYY" css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label="Комментарий" name="notes">
            <Input.TextArea rows={4} placeholder="Причина корректировки или примечание" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Настройки грядки"
        open={bedSettingsOpen}
        onOk={handleSaveBedSettings}
        onCancel={() => setBedSettingsOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={bedForm} layout="vertical">
          <Form.Item
            label="Название грядки"
            name="name"
            rules={[{ required: true, message: 'Введите название грядки' }]}
          >
            <Input placeholder="Грядка A1B1" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Состояние почвы"
        open={soilStateOpen}
        onOk={handleSaveSoilState}
        onCancel={() => setSoilStateOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingSoilState}
      >
        <Form form={soilStateForm} layout="vertical">
          <Form.Item label="Источник" name="sourceType" initialValue="manual">
            <Select
              options={Object.entries(SOIL_SOURCE_LABELS)
                .filter(([value]) => value !== 'lab')
                .map(([value, label]) => ({ value, label }))}
            />
          </Form.Item>
          <Form.Item label="Дата наблюдения" name="observedAt">
            <Input placeholder="2026-03-20T12:00:00Z" />
          </Form.Item>
          <Form.Item label="Доверие, %" name="confidencePct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          {SOIL_METRIC_LABELS.map((item) => (
            <Form.Item key={item.key} label={item.label} name={item.key}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ))}
          <Form.Item label="Примечание" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Новый замер почвы"
        open={soilMeasurementOpen}
        onOk={handleCreateSoilMeasurement}
        onCancel={() => setSoilMeasurementOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingSoilMeasurement}
      >
        <Form form={soilMeasurementForm} layout="vertical">
          <Form.Item label="Источник" name="sourceType" initialValue="measured">
            <Select
              options={Object.entries(SOIL_SOURCE_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </Form.Item>
          <Form.Item label="Дата замера" name="measuredAt">
            <Input placeholder="2026-03-20T12:00:00Z" />
          </Form.Item>
          <Form.Item label="Глубина от, см" name="sampleDepthFromCm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Глубина до, см" name="sampleDepthToCm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Доверие, %" name="confidencePct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          {SOIL_METRIC_LABELS.map((item) => (
            <Form.Item key={item.key} label={item.label} name={item.key}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ))}
          <Form.Item label="Примечание" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>


      <PlantingFormModal
        open={createModalOpen}
        title="Новая посадка"
        confirmText="Создать"
        loading={creatingPlanting}
        farmId={resolvedFarmId}
        defaultLocations={defaultPlantingLocations}
        onCancel={() => setCreateModalOpen(false)}
        onSubmit={handleCreatePlantingSubmit}
      />

      <PlantingFormModal
        open={editModalOpen}
        title="Изменить посадку"
        confirmText="Сохранить"
        loading={updatingPlanting}
        farmId={resolvedFarmId}
        initialPlanting={editingPlanting}
        defaultLocations={defaultPlantingLocations}
        submitLocations={false}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingPlanting(null);
        }}
        onSubmit={handleUpdatePlantingSubmit}
      />
    </MainLayout>
  );
};

const BedPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioBedPage />;
  }
  return <FarmBoundBedPage />;
};

export default BedPage;
